import { motion } from "framer-motion";

export function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#03001C] to-[#090024]">
      <main className="container mx-auto px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="prose prose-invert max-w-3xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent mb-8">
            Privacy Policy
          </h1>

          <div className="space-y-8 text-white/80">
            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Information We Collect
              </h2>
              <p className="text-white/80">
                We collect information that you provide directly to us,
                including when you:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>Create an account or sign up for our services</li>
                <li>Use our code review and analysis features</li>
                <li>Contact our support team</li>
                <li>Subscribe to our newsletters or updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                How We Use Your Information
              </h2>
              <p className="text-white/80">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process your transactions and send related information</li>
                <li>Send you technical notices and security alerts</li>
                <li>Respond to your comments and questions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Data Security
              </h2>
              <p className="text-white/80">
                We implement appropriate technical and organizational measures
                to protect your personal information against unauthorized or
                unlawful processing, accidental loss, destruction, or damage.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Your Rights
              </h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Contact Us
              </h2>
              <p className="text-white/80">
                If you have any questions about this Privacy Policy, please
                contact us at{" "}
                <a
                  href="mailto:privacy@aicodereview.dev"
                  className="text-blue-400 hover:text-blue-300"
                >
                  privacy@aicodereview.dev
                </a>
              </p>
            </section>

            <section className="text-sm text-white/60">
              <p className="text-white/60">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                AI Processing & Code Analysis
              </h2>
              <p className="text-white/80">
                Our AI-powered code review service processes your code data in the following ways:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>Automated code analysis and review using machine learning models</li>
                <li>Temporary storage of code snippets for analysis purposes</li>
                <li>Pattern recognition for identifying code improvements</li>
                <li>Generation of improvement suggestions and explanations</li>
              </ul>
              <p className="text-white/80 mt-4">
                We retain code data for analysis for a maximum of 30 days, after which it is automatically deleted from our systems.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Code Privacy & Security
              </h2>
              <p className="text-white/80">
                We understand the sensitive nature of source code and implement strict measures to protect it:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>End-to-end encryption for all code transmissions</li>
                <li>Isolated processing environments for each user's code</li>
                <li>Strict access controls and authentication measures</li>
                <li>No persistent storage of complete source code</li>
                <li>Option to use private deployments for enterprise customers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                International Data Processing
              </h2>
              <p className="text-white/80">
                We comply with international data protection regulations including:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>GDPR (European Union)</li>
                <li>CCPA (California, USA)</li>
                <li>PIPEDA (Canada)</li>
                <li>LGPD (Brazil)</li>
              </ul>
              <p className="text-white/80 mt-4">
                Your data may be processed in the following locations:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>United States (Primary)</li>
                <li>European Union (For EU customers)</li>
                <li>Custom locations available for enterprise customers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Third-Party Integrations
              </h2>
              <p className="text-white/80">
                When you connect our service to third-party platforms (such as GitHub, GitLab, or Bitbucket):
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>We only access repositories you explicitly authorize</li>
                <li>Integration permissions can be revoked at any time</li>
                <li>We maintain separate privacy agreements with each platform</li>
                <li>Third-party access tokens are encrypted and securely stored</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Intellectual Property Rights
              </h2>
              <p className="text-white/80">
                We respect your intellectual property rights:
              </p>
              <ul className="list-disc pl-6 mt-2">
                <li>You retain all rights to your source code</li>
                <li>AI suggestions are provided as-is without claiming any IP rights</li>
                <li>We do not use your code to train our models without explicit consent</li>
                <li>You can request complete deletion of your code and associated data</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
